/**
 * The chat composer: attach button (file picker) + auto-growing textarea +
 * send. Send is disabled while streaming, while any attachment is still
 * uploading, or when there is nothing to send. Staged files render in the
 * AttachmentPreviews strip above the input.
 *
 * Input rules mirror the ChatGPT and Claude mobile apps (see composer-input.ts):
 * on a desktop Enter sends and Shift+Enter adds a line; on a phone the Return
 * key adds a line and the send button sends (Cmd/Ctrl+Enter sends anywhere).
 * Pasting an image or screenshot attaches it. Buttons are 44px on touch sizes
 * and the bar clears the iPhone home indicator.
 */
import {
  PaperclipIcon,
  PaperPlaneRightIcon,
  StopIcon,
} from '@phosphor-icons/react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useAttachments } from '@/features/chat/data-access/useAttachments';
import { useChat } from '@/features/chat/data-access/useChat';
import { Button, Textarea } from '@andavo/web-ui';
import { AttachmentPreviews } from './AttachmentPreviews';
import {
  filesFromTransfer,
  isCoarsePointer,
  shouldSendOnKey,
} from '../composer-input';

/** Tallest the input grows before it scrolls (matches the old max-h-40). */
const MAX_INPUT_PX = 160;

/**
 * The long hint wraps to three lines on a 390px phone and makes the empty box
 * a third of the screen taller than it needs to be. Phones get a one-line hint,
 * like the ChatGPT and Claude apps.
 */
const PLACEHOLDER_DESKTOP = 'Ask about fare rules, exchanges, Sabre commands…';
const PLACEHOLDER_PHONE = 'Ask a question…';

export function Composer() {
  const { send, cancel, isStreaming } = useChat();
  const { pendingAttachments, addFiles, removeAttachment, uploading } =
    useAttachments();
  const [value, setValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const hasReadyAttachment = pendingAttachments.some((a) => !a.uploading);
  const canSend =
    !isStreaming &&
    !uploading &&
    (value.trim().length > 0 || hasReadyAttachment);

  // Auto-grow: collapse, then fit to content up to MAX_INPUT_PX. Runs after
  // every value change, so clearing the box on send shrinks it back to one line.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_PX)}px`;
  }, [value]);

  const submit = () => {
    if (!canSend) return;
    void send(value);
    setValue('');
  };

  return (
    <div className="border-border-subtle border-t bg-surface-background p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <AttachmentPreviews
        attachments={pendingAttachments}
        onRemove={removeAttachment}
      />
      <div className="mx-auto flex max-w-3xl items-end gap-2">
        <input
          accept="image/*,.pdf,.txt,.csv,.xls,.xlsx"
          className="hidden"
          // data-testid="chat-attach-input" — the hidden file input the e2e
          // attachments spec drives via setInputFiles (selectors.ts attachInput).
          data-testid="chat-attach-input"
          multiple
          onChange={(e) => {
            const files = e.target.files ? Array.from(e.target.files) : [];
            if (files.length > 0) void addFiles(files);
            // Reset so selecting the same file again re-triggers change.
            e.target.value = '';
          }}
          ref={fileInputRef}
          type="file"
        />
        <Button
          aria-label="Attach file"
          className="max-lg:size-11"
          // data-testid="chat-attach-button" — e2e attachments spec attach
          // affordance (selectors.ts attachButton).
          data-testid="chat-attach-button"
          disabled={isStreaming}
          onClick={() => fileInputRef.current?.click()}
          size="icon"
          variant="outline"
        >
          <PaperclipIcon className="size-icon-sm" />
        </Button>
        <Textarea
          className="max-h-40 min-h-[44px] flex-1 resize-none"
          // data-testid="chat-composer-input" — the composer textarea the e2e
          // chat spec types into (selectors.ts chatComposer). Nomad Textarea
          // spreads ...props onto the <textarea>, so this forwards cleanly.
          data-testid="chat-composer-input"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (shouldSendOnKey(e.nativeEvent, isCoarsePointer())) {
              e.preventDefault();
              submit();
            }
          }}
          onPaste={(e) => {
            // Screenshots and copied images arrive as files. Only intercept the
            // paste when files are present, so pasting text still works.
            const files = filesFromTransfer(e.clipboardData);
            if (files.length === 0) return;
            e.preventDefault();
            void addFiles(files);
          }}
          placeholder={
            isCoarsePointer() ? PLACEHOLDER_PHONE : PLACEHOLDER_DESKTOP
          }
          ref={inputRef}
          rows={1}
          value={value}
        />
        {isStreaming ? (
          // While streaming, the send affordance becomes a Stop button that
          // aborts the in-flight reply (Composer is where the user's eyes are).
          // Esc also triggers this (see useKeyboardShortcuts).
          <Button
            aria-label="Stop generating"
            className="max-lg:size-11"
            // data-testid="chat-stop-button" — the abort action the e2e chat
            // spec clicks mid-stream (selectors.ts chatStopButton).
            data-testid="chat-stop-button"
            onClick={cancel}
            size="icon"
            variant="outline"
          >
            <StopIcon className="size-icon-sm" weight="fill" />
          </Button>
        ) : (
          <Button
            aria-label="Send"
            className="max-lg:size-11"
            // data-testid="chat-send-button" — the send action the e2e chat spec
            // clicks (selectors.ts chatSendButton).
            data-testid="chat-send-button"
            disabled={!canSend}
            onClick={submit}
            size="icon"
          >
            <PaperPlaneRightIcon className="size-icon-sm" />
          </Button>
        )}
      </div>
    </div>
  );
}
